import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;
const logError = (msg, err) => {
  if (process.env.NODE_ENV !== 'production') {
    console.error(msg, err);
  }
};

// Contact Form API
export const submitContact = async (contactData) => {
  try {
    const response = await axios.post(`${API}/contacts`, contactData);
    return response.data;
  } catch (error) {
    logError('Error submitting contact form:', error);
    throw error;
  }
};

export const getContacts = async (limit = 50) => {
  try {
    const response = await axios.get(`${API}/contact?limit=${limit}`);
    return response.data;
  } catch (error) {
    logError('Error fetching contacts:', error);
    throw error;
  }
};

// Volunteer API
export const submitVolunteer = async (volunteerData) => {
  try {
    const response = await axios.post(`${API}/volunteers`, volunteerData);
    return response.data;
  } catch (error) {
    logError('Error submitting volunteer application:', error);
    throw error;
  }
};

export const getVolunteers = async (limit = 50) => {
  try {
    const response = await axios.get(`${API}/volunteers?limit=${limit}`);
    return response.data;
  } catch (error) {
    logError('Error fetching volunteers:', error);
    throw error;
  }
};

// Prayer Request API
export const submitPrayerRequest = async (prayerData) => {
  try {
    const response = await axios.post(`${API}/prayer-requests`, prayerData);
    return response.data;
  } catch (error) {
    logError('Error submitting prayer request:', error);
    throw error;
  }
};

export const getPrayerRequests = async (limit = 10, isPublic = true) => {
  try {
    const response = await axios.get(`${API}/prayer-requests?limit=${limit}&is_public=${isPublic}`);
    return response.data;
  } catch (error) {
    logError('Error fetching prayer requests:', error);
    throw error;
  }
};

// Donation API
export const submitDonation = async (donationData) => {
  try {
    const response = await axios.post(`${API}/donations`, donationData);
    return response.data;
  } catch (error) {
    logError('Error submitting donation:', error);
    throw error;
  }
};

export const getDonations = async (limit = 50) => {
  try {
    const response = await axios.get(`${API}/donations?limit=${limit}`);
    return response.data;
  } catch (error) {
    logError('Error fetching donations:', error);
    throw error;
  }
};

// Stats API
export const getMinistryStats = async () => {
  try {
    const response = await axios.get(`${API}/stats`);
    return response.data;
  } catch (error) {
    logError('Error fetching ministry stats:', error);
    throw error;
  }
};

// Encounter Lessons API
export const getLessons = async (limit = 50, publishedOnly = true) => {
  try {
    const response = await axios.get(`${API}/lessons?limit=${limit}&published_only=${publishedOnly}`);
    return response.data;
  } catch (error) {
    logError('Error fetching lessons:', error);
    throw error;
  }
};

export const getLesson = async (lessonId) => {
  try {
    const response = await axios.get(`${API}/lessons/${lessonId}`);
    return response.data;
  } catch (error) {
    logError('Error fetching lesson:', error);
    throw error;
  }
};

// Lesson Comments API
export const submitComment = async (commentData) => {
  try {
    const response = await axios.post(`${API}/comments`, commentData);
    return response.data;
  } catch (error) {
    logError('Error submitting comment:', error);
    throw error;
  }
};

export const getComments = async (lessonId, limit = 100) => {
  try {
    const response = await axios.get(`${API}/comments/${lessonId}?limit=${limit}`);
    return response.data;
  } catch (error) {
    logError('Error fetching comments:', error);
    throw error;
  }
};

// Reading Revelation API
export const createRevelation = async (revelationData) => {
  try {
    const response = await axios.post(`${API}/revelations`, revelationData);
    return response.data;
  } catch (error) {
    logError('Error creating revelation:', error);
    throw error;
  }
};

export const getRevelations = async (limit = 50, publishedOnly = true) => {
  try {
    const response = await axios.get(`${API}/revelations?limit=${limit}&published_only=${publishedOnly}`);
    return response.data;
  } catch (error) {
    logError('Error fetching revelations:', error);
    throw error;
  }
};

export const getRevelation = async (revelationId) => {
  try {
    const response = await axios.get(`${API}/revelations/${revelationId}`);
    return response.data;
  } catch (error) {
    logError('Error fetching revelation:', error);
    throw error;
  }
};

// Revelation Comments API
export const submitRevelationComment = async (commentData) => {
  try {
    const response = await axios.post(`${API}/revelation-comments`, commentData);
    return response.data;
  } catch (error) {
    logError('Error submitting revelation comment:', error);
    throw error;
  }
};

export const getRevelationComments = async (revelationId, limit = 100) => {
  try {
    const response = await axios.get(`${API}/revelation-comments/${revelationId}?limit=${limit}`);
    return response.data;
  } catch (error) {
    logError('Error fetching revelation comments:', error);
    throw error;
  }
};

// Health Check
export const healthCheck = async () => {
  try {
    const response = await axios.get(`${API}/`);
    return response.data;
  } catch (error) {
    logError('Error checking API health:', error);
    throw error;
  }
};

// Stripe Payment API
export const createPaymentCheckout = async (paymentData) => {
  try {
    const response = await axios.post(`${API}/payments/checkout`, paymentData);
    return response.data;
  } catch (error) {
    logError('Error creating payment checkout:', error);
    throw error;
  }
};

export const getPaymentStatus = async (sessionId) => {
  try {
    const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
    return response.data;
  } catch (error) {
    logError('Error fetching payment status:', error);
    throw error;
  }
};


// Lessons/Encounters Admin Management API
export const createLesson = async (lessonData, token) => {
  try {
    const response = await axios.post(`${API}/lessons`, lessonData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error creating lesson:', error);
    throw error;
  }
};

export const updateLesson = async (lessonId, lessonData, token) => {
  try {
    const response = await axios.put(`${API}/lessons/${lessonId}`, lessonData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error updating lesson:', error);
    throw error;
  }
};

export const deleteLesson = async (lessonId, token) => {
  try {
    const response = await axios.delete(`${API}/lessons/${lessonId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error deleting lesson:', error);
    throw error;
  }
};


// Admin Authentication API
export const adminLogin = async (credentials) => {
  try {
    const response = await axios.post(`${API}/admin/login`, credentials);
    return response.data;
  } catch (error) {
    logError('Error during admin login:', error);
    throw error;
  }
};

// News API
export const getNews = async (limit = 50, publishedOnly = true) => {
  try {
    const response = await axios.get(`${API}/news?limit=${limit}&published_only=${publishedOnly}`);
    return response.data;
  } catch (error) {
    logError('Error fetching news:', error);
    throw error;
  }
};

export const getNewsPost = async (newsId) => {
  try {
    const response = await axios.get(`${API}/news/${newsId}`);
    return response.data;
  } catch (error) {
    logError('Error fetching news post:', error);
    throw error;
  }
};

export const createNews = async (newsData, token) => {
  try {
    const response = await axios.post(`${API}/news`, newsData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error creating news:', error);
    throw error;
  }
};

export const updateNews = async (newsId, newsData, token) => {
  try {
    const response = await axios.put(`${API}/news/${newsId}`, newsData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error updating news:', error);
    throw error;
  }
};

export const deleteNews = async (newsId, token) => {
  try {
    const response = await axios.delete(`${API}/news/${newsId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error deleting news:', error);
    throw error;
  }
};

export const uploadFile = async (file, token) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    logError('Error uploading file:', error);
    throw error;
  }
};

// Blog API
export const getBlogPosts = async (limit = 50, publishedOnly = true) => {
  try {
    const response = await axios.get(`${API}/blog?limit=${limit}&published_only=${publishedOnly}`);
    return response.data;
  } catch (error) {
    logError('Error fetching blog posts:', error);
    throw error;
  }
};

export const getBlogPost = async (postId) => {
  try {
    const response = await axios.get(`${API}/blog/${postId}`);
    return response.data;
  } catch (error) {
    logError('Error fetching blog post:', error);
    throw error;
  }
};

export const createBlogPost = async (postData, token) => {
  try {
    const response = await axios.post(`${API}/blog`, postData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error creating blog post:', error);
    throw error;
  }
};

export const updateBlogPost = async (postId, postData, token) => {
  try {
    const response = await axios.put(`${API}/blog/${postId}`, postData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error updating blog post:', error);
    throw error;
  }
};

export const deleteBlogPost = async (postId, token) => {
  try {
    const response = await axios.delete(`${API}/blog/${postId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error deleting blog post:', error);
    throw error;
  }
};

// Subscribers API
export const subscribeToBlog = async (data) => {
  try {
    const response = await axios.post(`${API}/subscribers`, data);
    return response.data;
  } catch (error) {
    logError('Error subscribing:', error);
    throw error;
  }
};

export const getSubscribers = async (token) => {
  try {
    const response = await axios.get(`${API}/admin/subscribers`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error fetching subscribers:', error);
    throw error;
  }
};

export const deleteSubscriber = async (id, token) => {
  try {
    const response = await axios.delete(`${API}/admin/subscribers/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error removing subscriber:', error);
    throw error;
  }
};

// Testimonies API
export const submitTestimony = async (data) => {
  try {
    const response = await axios.post(`${API}/testimonies`, data);
    return response.data;
  } catch (error) {
    logError('Error submitting testimony:', error);
    throw error;
  }
};

export const getPublicTestimonies = async (limit = 20) => {
  try {
    const response = await axios.get(`${API}/testimonies?limit=${limit}`);
    return response.data;
  } catch (error) {
    logError('Error fetching testimonies:', error);
    throw error;
  }
};

export const getAdminTestimonies = async (token, status = null) => {
  try {
    const url = status ? `${API}/admin/testimonies?status=${status}` : `${API}/admin/testimonies`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error fetching admin testimonies:', error);
    throw error;
  }
};

export const updateTestimony = async (id, data, token) => {
  try {
    const response = await axios.put(`${API}/admin/testimonies/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error updating testimony:', error);
    throw error;
  }
};

export const deleteTestimony = async (id, token) => {
  try {
    const response = await axios.delete(`${API}/admin/testimonies/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error deleting testimony:', error);
    throw error;
  }
};

// Donation goal progress
export const getDonationProgress = async () => {
  try {
    const response = await axios.get(`${API}/donations/progress`);
    return response.data;
  } catch (error) {
    logError('Error fetching progress:', error);
    throw error;
  }
};

export const updateMonthlyGoal = async (goal, token) => {
  try {
    const response = await axios.put(`${API}/admin/settings/monthly-goal`,
      { goal },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    logError('Error updating goal:', error);
    throw error;
  }
};

// Loving You Back To Life - CRM
const authH = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export const getLybtlContacts = async (token, q = '') => {
  const url = q ? `${API}/admin/lybtl/contacts?q=${encodeURIComponent(q)}` : `${API}/admin/lybtl/contacts`;
  const response = await axios.get(url, authH(token));
  return response.data;
};

export const getLybtlContact = async (id, token) => {
  const response = await axios.get(`${API}/admin/lybtl/contacts/${id}`, authH(token));
  return response.data;
};

export const createLybtlContact = async (data, token) => {
  const response = await axios.post(`${API}/admin/lybtl/contacts`, data, authH(token));
  return response.data;
};

export const updateLybtlContact = async (id, data, token) => {
  const response = await axios.put(`${API}/admin/lybtl/contacts/${id}`, data, authH(token));
  return response.data;
};

export const deleteLybtlContact = async (id, token) => {
  const response = await axios.delete(`${API}/admin/lybtl/contacts/${id}`, authH(token));
  return response.data;
};

export const getLybtlJournal = async (contactId, token) => {
  const response = await axios.get(`${API}/admin/lybtl/contacts/${contactId}/journal`, authH(token));
  return response.data;
};

export const addJournalEntry = async (contactId, data, token) => {
  const response = await axios.post(`${API}/admin/lybtl/contacts/${contactId}/journal`, data, authH(token));
  return response.data;
};

export const updateJournalEntry = async (entryId, data, token) => {
  const response = await axios.put(`${API}/admin/lybtl/journal/${entryId}`, data, authH(token));
  return response.data;
};

export const deleteJournalEntry = async (entryId, token) => {
  const response = await axios.delete(`${API}/admin/lybtl/journal/${entryId}`, authH(token));
  return response.data;
};

export const getLybtlUpcoming = async (token) => {
  const response = await axios.get(`${API}/admin/lybtl/upcoming`, authH(token));
  return response.data;
};

// Admin Dashboard API
export const getDashboardStats = async (token) => {
  try {
    const response = await axios.get(`${API}/admin/dashboard/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error fetching dashboard stats:', error);
    throw error;
  }
};

export const getAdminDonations = async (token, filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.donation_type) params.append('donation_type', filters.donation_type);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.min_amount) params.append('min_amount', filters.min_amount);
    if (filters.max_amount) params.append('max_amount', filters.max_amount);
    
    const response = await axios.get(`${API}/admin/donations?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error fetching admin donations:', error);
    throw error;
  }
};

export const exportDonationsCSV = async (token) => {
  try {
    const response = await axios.get(`${API}/admin/donations/export`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    logError('Error exporting donations:', error);
    throw error;
  }
};

export const getAdminVolunteers = async (token) => {
  try {
    const response = await axios.get(`${API}/admin/volunteers`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error fetching volunteers:', error);
    throw error;
  }
};

export const getAdminContacts = async (token) => {
  try {
    const response = await axios.get(`${API}/admin/contacts`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error fetching contacts:', error);
    throw error;
  }
};

export const getAdminPrayerRequests = async (token) => {
  try {
    const response = await axios.get(`${API}/admin/prayer-requests`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error fetching prayer requests:', error);
    throw error;
  }
};

// --- Site enhancements: Impact stats, Prayer wall counter, Admin "Today" summary ---

export const getImpactStats = async () => {
  try {
    const response = await axios.get(`${API}/stats/impact`);
    return response.data;
  } catch (error) {
    logError('Error fetching impact stats:', error);
    throw error;
  }
};

export const updateImpactStats = async (payload) => {
  try {
    const token = localStorage.getItem('admin_token');
    const response = await axios.put(`${API}/admin/stats/impact`, payload, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error updating impact stats:', error);
    throw error;
  }
};

export const recordPrayer = async (prayerId) => {
  try {
    const response = await axios.post(`${API}/prayer-requests/${prayerId}/pray`);
    return response.data;
  } catch (error) {
    logError('Error recording prayer:', error);
    throw error;
  }
};

export const getAdminTodaySummary = async () => {
  try {
    const token = localStorage.getItem('admin_token');
    const response = await axios.get(`${API}/admin/today-summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error fetching today summary:', error);
    throw error;
  }
};

export const deletePrayerRequest = async (prayerId) => {
  try {
    const token = localStorage.getItem('admin_token');
    const response = await axios.delete(`${API}/admin/prayer-requests/${prayerId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error deleting prayer request:', error);
    throw error;
  }
};


// --- Candle Wall ---

export const lightCandle = async (payload) => {
  try {
    const response = await axios.post(`${API}/candles`, payload);
    return response.data;
  } catch (error) {
    logError('Error lighting candle:', error);
    throw error;
  }
};

export const getCandles = async (limit = 200) => {
  try {
    const response = await axios.get(`${API}/candles?limit=${limit}`);
    return response.data;
  } catch (error) {
    logError('Error fetching candles:', error);
    throw error;
  }
};

export const getPrayerCount = async () => {
  try {
    const response = await axios.get(`${API}/stats/prayer-count`);
    return response.data;
  } catch (error) {
    logError('Error fetching prayer count:', error);
    throw error;
  }
};

// --- Free Notary Services ---

export const submitNotaryRequest = async (data) => {
  try {
    const response = await axios.post(`${API}/notary-requests`, data);
    return response.data;
  } catch (error) {
    logError('Error submitting notary request:', error);
    throw error;
  }
};

export const getAdminNotaryRequests = async (token) => {
  try {
    const response = await axios.get(`${API}/admin/notary-requests`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error fetching notary requests:', error);
    throw error;
  }
};

export const deleteNotaryRequest = async (id, token) => {
  try {
    const response = await axios.delete(`${API}/admin/notary-requests/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error deleting notary request:', error);
    throw error;
  }
};

// --- Resource Directory ---

export const getResources = async (category) => {
  try {
    const params = category ? { category } : {};
    const response = await axios.get(`${API}/resources`, { params });
    return response.data;
  } catch (error) {
    logError('Error fetching resources:', error);
    throw error;
  }
};

export const getAdminResources = async (token) => {
  try {
    const response = await axios.get(`${API}/admin/resources`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error fetching admin resources:', error);
    throw error;
  }
};

export const createResource = async (data, token) => {
  try {
    const response = await axios.post(`${API}/admin/resources`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error creating resource:', error);
    throw error;
  }
};

export const updateResource = async (id, data, token) => {
  try {
    const response = await axios.put(`${API}/admin/resources/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error updating resource:', error);
    throw error;
  }
};

export const deleteResource = async (id, token) => {
  try {
    const response = await axios.delete(`${API}/admin/resources/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logError('Error deleting resource:', error);
    throw error;
  }
};

// --- Voices from the Street ---

export const submitVoiceTestimony = async (formData) => {
  const response = await axios.post(`${API}/voices`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const getApprovedVoices = async () => {
  const response = await axios.get(`${API}/voices`);
  return response.data;
};

export const getAdminVoices = async (token) => {
  const response = await axios.get(`${API}/admin/voices`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const updateVoice = async (id, data, token) => {
  const response = await axios.put(`${API}/admin/voices/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const deleteVoice = async (id, token) => {
  const response = await axios.delete(`${API}/admin/voices/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// --- Miracle Mailbox ---

export const getMailbox = async (code) => {
  const response = await axios.get(`${API}/mailbox/${code}`);
  return response.data;
};

export const generateMailboxCodes = async (payload, token) => {
  const response = await axios.post(`${API}/admin/mailbox/generate`, payload, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getAdminMailbox = async (token) => {
  const response = await axios.get(`${API}/admin/mailbox`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const updateMailboxCode = async (code, data, token) => {
  const response = await axios.put(`${API}/admin/mailbox/${code}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const deleteMailboxCode = async (code, token) => {
  const response = await axios.delete(`${API}/admin/mailbox/${code}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

